import { LocalizationString } from '@celito.clients/assets';
import { LayoutWithValueConfig } from '@celito.clients/types';
import { errorToast } from '@celito.clients/utils';
import { AxiosError } from 'axios';
import { Section } from 'libs/core/src/types/wizard-config';
import { Fragment, useCallback } from 'react';
import { FieldValues, UseFormSetError } from 'react-hook-form';

import { FieldApiError } from '../components/form-wizard/form-wizard.model';
import { useFormEngineContext } from './useFormEngineContext';

export interface ErrorMessage {
  location: string;
  message: string;
}

export interface ErrorResponseData {
  errorMessage: ErrorMessage[] | string[];
  errorTitle: string;
  statusCode: number;
}

const useFormApiErrorHandler = () => {
  const { setFieldsApiErrors } = useFormEngineContext();
  const handleFormApiError = useCallback(
    (
      error: unknown,
      sections: Section[],
      setError?: UseFormSetError<FieldValues>
    ) => {
      const fieldErrors: FieldApiError[] = [];
      const axiosError = error as AxiosError;
      const sectionsWithErrors = new Set<number>();

      if (axiosError.response?.data) {
        const errorResponse = axiosError.response.data as ErrorResponseData;
        const toastErrors: string[] = [];

        if (Array.isArray(errorResponse.errorMessage)) {
          errorResponse.errorMessage.forEach((error) => {
            let errorLocation: string;
            let errorMessage: string;

            // Handle both object format {location, message} and string format "location : message"
            if (typeof error === 'string') {
              const parts = error.split(' : ');
              if (parts.length >= 2) {
                errorLocation = parts[0].trim();
                errorMessage = parts.slice(1).join(' : ').trim();
              } else {
                // If no colon separator, treat entire string as message
                errorLocation = '';
                errorMessage = error;
              }
            } else {
              errorLocation = error.location;
              errorMessage = error.message;
            }

            const field = sections
              .flatMap((sec, index) =>
                sec.fields.map((field) => ({ field, index }))
              )
              .find(({ field }) => field.columnName === errorLocation)?.field;

            if (field) {
              const sectionIndex = sections.findIndex((sec) =>
                sec.fields.includes(field)
              );
              sectionsWithErrors.add(sectionIndex);
            }

            const isVisible =
              field?.layoutConfiguration?.defaultLayoutRules?.isVisible ??
              !!(
                field?.layoutConfiguration
                  ?.layoutConfig as LayoutWithValueConfig
              )?.value?.isVisible;

            if (isVisible && errorLocation) {
              setError?.(errorLocation, {
                type: 'server side',
                message: errorMessage,
              });
              fieldErrors.push({
                field: errorLocation,
                message: errorMessage,
              });
            } else {
              toastErrors.push(errorMessage);
            }
          });
        } else {
          toastErrors.push(errorResponse.errorMessage);
        }

        if (toastErrors.length > 1) {
          errorToast({
            title: errorResponse.errorTitle,
            message: (
              <ul>
                {toastErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            ),
          });
        } else {
          errorToast({
            title: errorResponse.errorTitle,
            message: toastErrors?.[0],
          });
        }
      }

      if (fieldErrors.length > 0) {
        if (!setError) {
          const sectionsIndexArray = Array.from(sectionsWithErrors);
          const sectionsLabels = sectionsIndexArray.map(
            (index) => sections[index].label
          );

          errorToast({
            title: LocalizationString.SUBMISION_FAILED,
            message: (
              <>
                <span>{LocalizationString.SUBMISION_ERROR}</span>
                {sectionsLabels.map((section) => (
                  <div key={section}>
                    {'- '}
                    {section}
                  </div>
                ))}
              </>
            ),
          });
        }

        setFieldsApiErrors?.({
          fields: fieldErrors,
          sections: Array.from(sectionsWithErrors),
        });
      }
    },
    [setFieldsApiErrors]
  );
  return { handleFormApiError };
};

export default useFormApiErrorHandler;
